// REFUSAL WORDING AND PLACEMENT (CR-20260910-001, contract M1 section 3, ruling Q-M5).
//
// The CODE is what crosses the wire; the WORDING lives here on the client. The list is CLOSED: a
// Unknown codes and technical failure details must never be displayed to the trader.
//
// EVERY REFUSAL HAS A PLACE, not just a sentence. A refusal shown at the Apply bar when it is
// really about one control makes the trader hunt for what he did wrong. `where` names the Setup
// control the message belongs beside, so the panel can render it IN PLACE — the way NoZone is
// required to appear beside the portfolio.
//
// Pure: no Angular, no DOM, so the mapping is unit-testable directly.

import {
    MAVEN_REFUSAL_CLOSE_SELECTION_EMPTY,
    MAVEN_REFUSAL_UNKNOWN_CLOSE_HEDGE_ID,
    MAVEN_REFUSAL_END_STRIKE_INSIDE_START,
    MAVEN_REFUSAL_EXPECTED_RANGE_UNAVAILABLE,
    MAVEN_REFUSAL_EXPIRED_HEDGES,
    MAVEN_REFUSAL_EXPIRED_POSITIONS,
    MAVEN_REFUSAL_MALFORMED_MARKET_OVERRIDES,
    MAVEN_REFUSAL_MALFORMED_SIDE_INPUTS,
    MAVEN_REFUSAL_MISSING_DATE,
    MAVEN_REFUSAL_MISSING_PORTFOLIO,
    MAVEN_REFUSAL_MISSING_TEMPLATE,
    MAVEN_REFUSAL_NO_HEDGE_SIDE,
    MAVEN_REFUSAL_NO_SAVED_POSITIONS,
    MAVEN_REFUSAL_NO_ZONE,
    MAVEN_REFUSAL_RANGE_EXCEEDS_CAP,
    MAVEN_REFUSAL_TEMPLATE_NOT_FOUND,
    MAVEN_REFUSALS
} from './maven-operations';

// Where a refusal is rendered. Each value is a Setup control (or the Apply bar) that the panel
// can address; nothing here is free text.
export type MavenRefusalPlacement =
    | 'portfolio'
    | 'template'
    | 'dates'
    | 'hedgeSides'
    // CR-20260916-003: the Existing Hedges section (the Close selection).
    | 'existingHedges'
    | 'callsCoverage'
    | 'putsCoverage'
    // CR-20260915-001: the Override Market Indicators section.
    | 'marketOverrides'
    | 'applyBar'
    // CR-20260916-001 (owner ruling 4): not a control at all — a WARNING TOAST. The Setup shows
    // it through the platform's toastr and paints no in-place line for it.
    | 'toast';

export interface MavenRefusal {
    code: string;
    placement: MavenRefusalPlacement;
    message: string;
    // Internal diagnostic flag; the displayed message never contains the unknown code.
    isDefect: boolean;
}

// The portfolio has no zone. THE WORDING IS THE OWNER'S OWN, quoted from the feature request and
// contract M1: "Maven cannot run without a zone". Do not paraphrase it.
// Exported since 2026-09-14: the Setup validator refuses the same condition BEFORE Apply (the
// Current Zone line, vrequest 2026-09-14) and must say it in the same words.
export const NO_ZONE_MESSAGE = 'Maven cannot run without a zone.';
export const MAVEN_GENERIC_ERROR = 'An error occurred during the request.';

const TABLE: { [code: string]: { placement: MavenRefusalPlacement; message: string } } = {};

TABLE[MAVEN_REFUSAL_MISSING_PORTFOLIO] = {
    placement: 'portfolio',
    message: 'Choose a portfolio first.'
};
TABLE[MAVEN_REFUSAL_NO_ZONE] = {
    placement: 'portfolio',
    message: NO_ZONE_MESSAGE
};
TABLE[MAVEN_REFUSAL_NO_SAVED_POSITIONS] = {
    // Placed on the PORTFOLIO, not the template: it is the portfolio that has nothing saved, and
    // the backend read it by the portfolio's id.
    placement: 'portfolio',
    message: 'This portfolio has no saved positions to roll.'
};
TABLE[MAVEN_REFUSAL_MISSING_TEMPLATE] = {
    placement: 'template',
    message: 'Choose an adjustment template.'
};
TABLE[MAVEN_REFUSAL_TEMPLATE_NOT_FOUND] = {
    placement: 'template',
    message: 'That adjustment template is no longer available for this portfolio. Choose another.'
};
TABLE[MAVEN_REFUSAL_MISSING_DATE] = {
    placement: 'dates',
    message: 'Choose a date, or a date range.'
};
TABLE[MAVEN_REFUSAL_RANGE_EXCEEDS_CAP] = {
    placement: 'dates',
    message: 'That range is wider than the limit. Narrow it and try again.'
};
TABLE[MAVEN_REFUSAL_NO_HEDGE_SIDE] = {
    placement: 'hedgeSides',
    message: 'Fill in the calls, the puts, or both. One side is enough; neither is not.'
};
TABLE[MAVEN_REFUSAL_MALFORMED_SIDE_INPUTS] = {
    placement: 'hedgeSides',
    message: 'One of the hedge sides is incomplete. Check the coverage, the offset, the minimum profit and the risk.'
};
// CR-20260915-001. The same words the Setup validator uses BEFORE Apply (OVERRIDES_INCOMPLETE_MESSAGE
// there), so a backend refusal reads exactly like the local one.
TABLE[MAVEN_REFUSAL_MALFORMED_MARKET_OVERRIDES] = {
    placement: 'marketOverrides',
    message: 'An overridden date is incomplete. Enter IV and the vertical skew of every enabled side, all greater than zero.'
};

// CR-20260916-001 (owner screencast 2026-09-16). An expired option cannot be priced, so the
// backend refuses the run before any supplier is ordered. Owner rulings: a TOAST, not an
// in-place line (4); the sentence only, the tickers stay in the shell log (3); the cutoff is
// 16:20 ET on the expiration date (2). ExpiredHedges is raised only when Existing Hedges = Close.
TABLE[MAVEN_REFUSAL_EXPIRED_POSITIONS] = {
    placement: 'toast',
    message: 'Some portfolio positions have expired. Fix them in the Adjustment Pricing Grid first, then run Maven.'
};
TABLE[MAVEN_REFUSAL_EXPIRED_HEDGES] = {
    placement: 'toast',
    message: 'Some existing hedges have expired. Fix them in the Hedging Grid first, then run Maven.'
};

// CR-20260916-003 (owner screencast 2026-09-16). Close is a selection. The Setup refuses the
// first before Apply in the same words; the second means the book changed under the panel.
TABLE[MAVEN_REFUSAL_CLOSE_SELECTION_EMPTY] = {
    placement: 'existingHedges',
    message: 'Close is selected but no expiration is chosen. Choose an expiration or switch to Ignore.'
};
TABLE[MAVEN_REFUSAL_UNKNOWN_CLOSE_HEDGE_ID] = {
    placement: 'existingHedges',
    message: 'A selected hedge is no longer on the saved book. Re-select the hedges to close.'
};

// The two side-specific ones. The backend's code does not say WHICH side, so both resolve to the
// calls control by default and the caller narrows it with `placementForSide` below when it knows.
TABLE[MAVEN_REFUSAL_EXPECTED_RANGE_UNAVAILABLE] = {
    placement: 'callsCoverage',
    message: 'Expected Range needs your Expected Move setting, and it cannot produce a range right now. Choose Range or Follow-Through.'
};
TABLE[MAVEN_REFUSAL_END_STRIKE_INSIDE_START] = {
    placement: 'callsCoverage',
    message: 'That end strike is at or inside where the hedge starts. Move it further out.'
};

// The two codes above are per side, but the wire code is not. When the caller knows which side
// raised it, this narrows the placement; unknown side keeps the default.
export function placementForSide(
    placement: MavenRefusalPlacement,
    side: 'calls' | 'puts' | null
): MavenRefusalPlacement {
    if (side !== 'puts') {
        return placement;
    }
    if (placement === 'callsCoverage') {
        return 'putsCoverage';
    }
    return placement;
}

// Resolve a wire code to something the panel can render. NEVER returns null and never returns an
// empty message: a silent empty is the one outcome the feature request rules out explicitly.
export function resolveMavenRefusal(code: string | null, failureDetail?: string | null): MavenRefusal {
    const known = code && Object.prototype.hasOwnProperty.call(TABLE, code) ? TABLE[code] : null;

    if (known) {
        return {code: code, placement: known.placement, message: known.message, isDefect: false};
    }

    // Only the known recovery hints above are suitable for display. Never echo arbitrary
    // backend text, including from an older backend, or an unrecognised refusal code.
    return {
        code: code || '',
        placement: 'applyBar',
        message: MAVEN_GENERIC_ERROR,
        isDefect: !!code
    };
}

// Every code on the contract's closed list has an entry. Stated as a function so the spec can
// assert it over MAVEN_REFUSALS rather than over a hand-kept copy of the list: add a refusal to
// the contract, forget the wording, and the spec goes red instead of the trader seeing the
// "unrecognised code" defect message in production.
export function refusalCodesWithoutWording(): string[] {
    return MAVEN_REFUSALS.filter(c => !TABLE[c]);
}
